import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CACHE_KEY, IS_CACHE_ENABLED } from '@app/core/interceptors/cache.interceptor';
import { APIResponse } from '@app/core/models/api.model';
import { Observable, catchError, throwError } from 'rxjs';
import { IS_SECURE_REQUEST } from '@app/core/interceptors/auth-token.interceptor';
import { SUPRESS_ERRORS } from '@app/core/interceptors/error.interceptor';

export class HttpMethod {
  static readonly GET = 'GET';
  static readonly POST = 'POST';
  static readonly PATCH = 'PATCH';
  static readonly PUT = 'PUT';
  static readonly DELETE = 'DELETE';
}

export type HttpMethodType = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

type HttpServiceRequest = {
  method: HttpMethodType;
  url: string;
  params?: HttpParams;
  body?: object | any[];
  headers?: HttpHeaders;
  cache?: boolean;
  cacheKey?: string;
  suppressErrors?: string[] | boolean;
};

@Injectable({ providedIn: 'root' })
export class HttpService {
  constructor(private http: HttpClient) {}

  sendRequest<T>({
    method,
    url,
    params,
    body,
    headers,
    cacheKey,
    cache
  }: HttpServiceRequest): Observable<HttpResponse<APIResponse<T>>> {
    headers = headers || new HttpHeaders();

    const httpCtx = new HttpContext();
    httpCtx.set(IS_CACHE_ENABLED, !!cache);
    httpCtx.set(IS_SECURE_REQUEST, false);
    if (cacheKey) {
      httpCtx.set(CACHE_KEY, cacheKey);
    }

    return this.handleRequest<APIResponse<T>>(
      this.http.request<APIResponse<T>>(method, url, {
        context: httpCtx,
        body,
        headers,
        params,
        observe: 'response'
      })
    );
  }

  sendSecureRequest<T>({
    method,
    url,
    params,
    body,
    headers,
    cache,
    cacheKey,
    suppressErrors
  }: HttpServiceRequest): Observable<HttpResponse<APIResponse<T>>> {
    headers = headers || new HttpHeaders();

    const httpCtx = new HttpContext();
    httpCtx.set(IS_CACHE_ENABLED, !!cache);
    httpCtx.set(IS_SECURE_REQUEST, true);
    if (cacheKey) {
      httpCtx.set(CACHE_KEY, cacheKey);
    }

    if (suppressErrors) {
      httpCtx.set(SUPRESS_ERRORS, suppressErrors);
    }

    return this.handleRequest<APIResponse<T>>(
      this.http.request<APIResponse<T>>(method, url, {
        context: httpCtx,
        body,
        headers,
        params,
        observe: 'response'
      })
    );
  }

  private handleRequest<T>(req: Observable<HttpResponse<T>>): Observable<HttpResponse<T>> {
    return req.pipe(
      catchError((response: HttpErrorResponse) => {
        return throwError(() => response);
      })
    );
  }
}
